import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback, useMemo, useRef } from "react";
import type { DataProvider } from "ra-core";

import { canAccess } from "../providers/commons/canAccess";
import type { AppRole, Contact, LeadSource, Project, Tag } from "../types";
import { createEachRow } from "../dataImport/createEachRow";
import { fetchRecordsWithCache } from "../dataImport/fetchRecordsWithCache";
import { toText } from "../dataImport/parseCell";
import { useCompanyResolver } from "../dataImport/useCompanyResolver";
import {
  parseContactTypes,
  resolveBusinessUnit,
  resolveOrigin,
  resolveProject,
} from "./contactImportRules";
import { splitSurnames, validateContactMandatory } from "./contactModel";

/**
 * Almikia Epic EC2a: the contacts CSV columns. `status` and `linkedin_url` are gone; the new
 * columns are `second_last_name`, `contact_types` (comma-separated English identifiers or Spanish
 * labels), `origin` (a lead source name), `business_unit` and `project` (BPO only).
 */
export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  second_last_name: string;
  gender: string;
  title: string;
  company: string;
  email_work: string;
  email_home: string;
  email_other: string;
  phone_work: string;
  phone_home: string;
  phone_other: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  has_newsletter: string;
  tags: string;
  contact_types: string;
  origin: string;
  business_unit: string;
  project: string;
};

/** Builds a rejection whose message names the row (its position in the batch). */
const rejectRow = (rowNumber: number, message: string) =>
  Promise.reject(new Error(`Fila ${rowNumber}: ${message}`));

export function useContactImport() {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  const getCompanies = useCompanyResolver();
  const getLeadSources = useCatalogueCache<LeadSource>("lead_sources");
  const getProjects = useCatalogueCache<Project>("projects");

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);
  // Only direccion may create tags (RLS tags_write): everyone else resolves existing names.
  const canCreateTags = canAccess((user?.identity?.role ?? "") as AppRole, {
    action: "create",
    resource: "tags",
  });
  const getTags = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        canCreateTags
          ? (name) => ({
              name,
              color: "#f9f9f9",
            })
          : undefined,
        dataProvider,
      ),
    [tagsCache, dataProvider, canCreateTags],
  );

  const processBatch = useCallback(
    async (batch: ContactImportSchema[]) => {
      const [companies, tags, leadSources, projects] = await Promise.all([
        getCompanies(
          batch
            .map((contact) => contact.company?.trim())
            .filter((name) => name),
        ),
        getTags(batch.flatMap((batch) => parseTags(batch.tags))),
        getLeadSources(),
        getProjects(),
      ]);

      return createEachRow(
        batch.map((row, index) => {
          const rowNumber = index + 1;
          const {
            gender,
            title,
            email_work,
            email_home,
            email_other,
            phone_work,
            phone_home,
            phone_other,
            background,
            first_seen,
            last_seen,
            has_newsletter,
            company: companyName,
            tags: tagNames,
          } = row;

          // Names: a file that only has `last_name` is split into the two surnames and the
          // contact is flagged for a person to review the split.
          const lastName = toText(row.last_name);
          const secondLastName = toText(row.second_last_name);
          const isLegacyName = secondLastName === undefined;
          const split = isLegacyName ? splitSurnames(lastName) : undefined;
          const names = {
            first_name: toText(row.first_name) ?? "",
            last_name: split ? (split.paterno ?? "") : (lastName ?? ""),
            second_last_name: split ? split.materno : secondLastName,
          };

          const typesResult = parseContactTypes(toText(row.contact_types));
          if ("error" in typesResult) {
            return rejectRow(rowNumber, typesResult.error);
          }
          const originResult = resolveOrigin(toText(row.origin), leadSources);
          if ("error" in originResult) {
            return rejectRow(rowNumber, originResult.error);
          }
          const unitResult = resolveBusinessUnit(toText(row.business_unit));
          if ("error" in unitResult) {
            return rejectRow(rowNumber, unitResult.error);
          }
          const projectResult = resolveProject(toText(row.project), projects);
          if ("error" in projectResult) {
            return rejectRow(rowNumber, projectResult.error);
          }

          const business_unit = unitResult.value;
          const contact: Partial<Contact> = {
            ...names,
            contact_types: typesResult.value,
            lead_source_id: originResult.value?.id ?? null,
            business_unit: business_unit ?? null,
            business_project_id:
              business_unit === "bpo"
                ? (projectResult.value?.id ?? null)
                : null,
          };

          // Reject a half-filled row with the same Spanish message the form shows.
          const errors = validateContactMandatory(contact, {
            originLinkKind: originResult.value?.link_kind ?? null,
          });
          const [firstError] = Object.values(errors);
          if (firstError) return rejectRow(rowNumber, firstError);

          const email_jsonb = [
            { email: email_work, type: "Work" },
            { email: email_home, type: "Home" },
            { email: email_other, type: "Other" },
          ].filter(({ email }) => email);
          const phone_jsonb = [
            { number: phone_work, type: "Work" },
            { number: phone_home, type: "Home" },
            { number: phone_other, type: "Other" },
          ].filter(({ number }) => number);
          const company = companyName?.trim()
            ? companies.get(companyName.trim())
            : undefined;
          const tagList = parseTags(tagNames)
            .map((name) => tags.get(name))
            .filter((tag): tag is Tag => !!tag);
          const unknownTag = parseTags(tagNames).find(
            (name) => !tags.get(name),
          );
          if (unknownTag) {
            return rejectRow(
              rowNumber,
              `La etiqueta "${unknownTag}" no existe y solo Direccion puede crearla`,
            );
          }

          return dataProvider.create("contacts", {
            data: {
              ...contact,
              name_needs_review: isLegacyName,
              gender,
              title,
              email_jsonb,
              phone_jsonb,
              background,
              first_seen: first_seen
                ? new Date(first_seen).toISOString()
                : today,
              last_seen: last_seen ? new Date(last_seen).toISOString() : today,
              has_newsletter,
              company_id: company?.id,
              tags: tagList.map((tag) => tag.id),
              // The importer never assigns a contact to another person.
              sales_id: user?.identity?.id,
            },
          });
        }),
      );
    },
    [
      dataProvider,
      getCompanies,
      getLeadSources,
      getProjects,
      getTags,
      user?.identity?.id,
      today,
    ],
  );

  return processBatch;
}

/**
 * A small catalogue (lead sources, projects) read once per data provider and reused by every
 * batch of the import.
 */
function useCatalogueCache<T extends { id: unknown }>(resource: string) {
  const dataProvider = useDataProvider();
  const cache = useRef<{ provider: DataProvider; rows: T[] }>(null);

  return useCallback(async (): Promise<T[]> => {
    if (cache.current?.provider !== dataProvider) {
      const { data } = await dataProvider.getList<T & { id: number }>(
        resource,
        {
          filter: {},
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "id", order: "ASC" },
        },
      );
      cache.current = { provider: dataProvider, rows: data };
    }
    return cache.current.rows;
  }, [dataProvider, resource]);
}

const parseTags = (tags: string) =>
  tags
    ?.split(",")
    ?.map((tag: string) => tag.trim())
    ?.filter((tag: string) => tag) ?? [];
